import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { IPagedQueryOptions } from "../library/models/page";
import {
    CreateEvaluationRequest,
    IEvaluation,
    IEvaluationSummary,
    IGdprConsent,
} from "../library/models/evaluation";
import { BaseService } from "./baseService";

/**
 * Patient evaluations: the intake sheet, the treatment consent signed with it, and whether
 * the patient has agreed to their data being processed here at all.
 *
 * Tenant-scoped like UsersService -- which clinic comes from the token, never from an
 * argument.
 */
export class EvaluationsService extends BaseService {
    /**
     * One patient's history, newest first, a page at a time.
     *
     * Paged because a patient accumulates sheets for as long as they are treated: there
     * is no count at which this list stops growing, so there is no count at which
     * fetching all of it stays reasonable. `size` is the caller's window -- the API caps
     * it and clamps anything silly.
     */
    getEvaluationsForPatient(patientId: number, size: number): IPagedQueryOptions<IEvaluationSummary> {
        return this.createGetInfiniteQuery<IEvaluationSummary>('/evaluations', size, { patientId });
    }

    getEvaluation(id: number): UseQueryOptions<IEvaluation, Error> {
        return this.createGetQuery<IEvaluation>(`/evaluations/${id}`);
    }

    /**
     * Whether this patient has already agreed to the clinic processing their data, so the
     * intake wizard knows whether to ask. Null means they have not, which is ordinary --
     * an admin can invite somebody nobody then gets as far as evaluating.
     *
     * Hand-written rather than a createGetQuery for one reason: the API answers 204 for
     * "none on file", and axios hands back an empty string for a body-less response. Left
     * alone that reaches the component as `''` -- falsy, but not the absence the caller is
     * checking for. Normalised to null here, because React Query refuses `undefined` as
     * query data and would rather the query had failed.
     */
    getGdprConsent(patientId: number): UseQueryOptions<IGdprConsent | null, Error> {
        const url = '/evaluations/gdpr-consent';

        return {
            queryKey: [url, { patientId }],
            queryFn: async (): Promise<IGdprConsent | null> => {
                const response = await this.api.get<IGdprConsent | ''>(url, { params: { patientId } });
                return response.data || null;
            },
        };
    }

    /**
     * Records the evaluation. On the server this also creates and invites the patient's
     * account, renders the PDF and emails the patient a link to it -- so a success here
     * means considerably more has happened than a row being written.
     */
    createEvaluationMutation(): UseMutationOptions<IEvaluation, Error, CreateEvaluationRequest> {
        return this.createMutation<IEvaluation, CreateEvaluationRequest>('/evaluations');
    }

    /**
     * Withdraws a sheet. Administrators only, and soft: the server keeps the row and the signed
     * PDF, and simply stops returning either -- from the lists, from the PDF endpoint and from
     * the patient's emailed link.
     *
     * Hand-written rather than createMutation(url, 'delete'), which sends the id as `?id=`.
     */
    deleteEvaluationMutation(): UseMutationOptions<void, Error, number> {
        return {
            mutationFn: (evaluationId: number): Promise<void> =>
                this.delete<void>(`/evaluations/${evaluationId}`),
        };
    }

    /**
     * The PDF, as a blob for the therapist to open or print.
     *
     * Hand-written rather than a createGetQuery: the response is binary, so it needs
     * responseType 'blob'. Without it axios parses the bytes as text and the file is
     * corrupt by the time anything can save it.
     */
    async fetchEvaluationPdf(id: number): Promise<Blob> {
        return this.get<Blob>(`/evaluations/${id}/pdf`, { responseType: 'blob' });
    }
}
